/**
 * <docs-mood> -- mood-cycle button for the docs site.
 *
 * Cycles --color-state-fill through three preset moods by toggling a
 * class on <html>:
 *
 *   chromatic  -- no class; docs.css :root override paints
 *                 --color-primary-subtle (the docs default)
 *   neutral    -- .mood-neutral on <html> overrides back to
 *                 --color-fill (Brio's library default)
 *   minimal    -- .mood-minimal on <html> overrides to transparent
 *                 (foreground-only indicator)
 *
 * Persists the choice to localStorage. The icon is a small circle
 * whose fill reflects the current mood, so the button shows the
 * active state without a label.
 *
 * Lives in src/docs/ rather than src/js/extras/ because the mood
 * cycle is a docs-only affordance: the library exposes the
 * --color-state-fill* tokens for consumers, and the docs site provides
 * this UI to demonstrate the effect. If it proves useful to
 * consumers it can promote to extras.
 *
 * Pair with the boot snippet in head.html so the saved class is
 * applied before first paint.
 */

import { BrioBase } from '../js/brio-base.js';

const STORAGE_KEY = 'docs-mood';

// Cycle advances through these in order and wraps at the end. The
// order runs loudest -> middle -> quietest, so the first click off
// the docs default takes the reader toward the library baseline.
const CYCLE_ORDER = ['chromatic', 'neutral', 'minimal'];

// Each icon is a 16x16 circle whose fill reflects the mood. The
// stroke uses currentColor so the outline tracks the surrounding
// chrome's text color in light and dark modes.
const ICONS = {
    minimal:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    neutral:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="var(--color-fill-stronger)" stroke="currentColor" stroke-width="1.5"/></svg>',
    chromatic:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="var(--color-primary-soft)" stroke="var(--color-primary)" stroke-width="1.5"/></svg>',
};

function nextMood(current) {
    const idx = CYCLE_ORDER.indexOf(current);
    return CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
}

function save(mood) {
    try {
        localStorage.setItem(STORAGE_KEY, mood);
    } catch {
        /* localStorage unavailable -- silent fallback */
    }
}

function loadMood() {
    try {
        return localStorage.getItem(STORAGE_KEY) || 'chromatic';
    } catch {
        return 'chromatic';
    }
}

function applyMood(mood) {
    const root = document.documentElement;
    root.classList.remove('mood-minimal', 'mood-neutral');
    if (mood === 'minimal' || mood === 'neutral') {
        root.classList.add(`mood-${mood}`);
    }
    // "chromatic" -- no class; the docs.css :root override carries it.

    document.querySelectorAll('docs-mood').forEach((el) => {
        el.setAttribute('data-current-mood', mood);
    });
}

class DocsMood extends BrioBase {
    static tag = 'docs-mood';
    static {
        customElements.define(DocsMood.tag, DocsMood);
    }

    #onClick;

    connectedCallback() {
        if (!this.children.length) {
            this.#renderDefault();
        }

        this.#onClick = (e) => {
            if (!e.target.closest('button')) return;
            const next = nextMood(loadMood());
            applyMood(next);
            save(next);
        };
        this.addEventListener('click', this.#onClick);

        applyMood(loadMood());
    }

    disconnectedCallback() {
        if (this.#onClick) this.removeEventListener('click', this.#onClick);
    }

    // Auto-rendered chrome -- a documented exception to Brio's
    // "custom elements orchestrate authored DOM" rule, same
    // justification as docs-themepicker's trigger: drop-in
    // convenience for a header affordance. Authored DOM is still
    // respected (skipped if children exist).
    #renderDefault() {
        const button = document.createElement('button');
        button.type = 'button';
        // Same chrome as the header's other icon buttons
        // (brio-color-mode's cycle button) so sizing and hover line up.
        button.className = 'button is-outlined is-sm is-icon';
        button.setAttribute('aria-label', 'Library mood (click to cycle)');
        button.setAttribute('title', 'Cycle the current-state fill mood');
        button.innerHTML = CYCLE_ORDER.map(
            (mood) => `<span data-icon-for="${mood}" aria-hidden="true">${ICONS[mood]}</span>`
        ).join('');
        this.appendChild(button);
    }
}
